import {
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Container,
  Grid,
  Group,
  Modal,
  Select,
  Skeleton,
  Stack,
  Text,
  Textarea,
  ThemeIcon,
  Title,
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { modals } from '@mantine/modals'
import { notifications } from '@mantine/notifications'
import { createFileRoute, Link } from '@tanstack/react-router'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  LuAnchor,
  LuArrowLeft,
  LuCalendar,
  LuCheck,
  LuClock,
  LuFileText,
  LuMapPin,
  LuShip,
  LuUser,
} from 'react-icons/lu'
import { CurrencyText } from '@/frontend/components/marina/CurrencyText'
import { CustomerAvatar } from '@/frontend/components/marina/CustomerAvatar'
import { KindBadge } from '@/frontend/components/marina/KindBadge'
import { StatusBadge } from '@/frontend/components/marina/StatusBadge'
import { useCanWrite } from '@/frontend/hooks/useAuth'
import { useBerths } from '@/frontend/hooks/useBerths'
import {
  type Booking,
  useApproveBooking,
  useAssignBerth,
  useBooking,
  useCancelBooking,
  useCheckIn,
  useCheckOut,
  useDeclineBooking,
  useWaitlistBooking,
} from '@/frontend/hooks/useBookings'

const FT_PER_METER = 3.28084

const CHANNEL_ID: Record<string, string> = {
  WALK_IN: 'Walk-in',
  RADIO: 'Radio',
  EMAIL: 'Email',
  PHONE: 'Telepon',
  TEXT_MESSAGE: 'Pesan',
  AGENT: 'Agen',
  OTHER: 'Lainnya',
}

const BOAT_TYPE_ID: Record<string, string> = {
  POWERBOAT: 'Powerboat',
  SAILING_BOAT: 'Kapal Layar',
  YACHT: 'Yacht',
  CATAMARAN: 'Katamaran',
  FISHING: 'Kapal Nelayan',
  DINGHY: 'Dinghy',
  OTHER: 'Lainnya',
}

export const Route = createFileRoute('/dashboard/reservations/$id')({
  component: BookingDetail,
})

function BookingDetail() {
  const { t } = useTranslation()
  const { id } = Route.useParams()
  const { data, isLoading } = useBooking(id)
  const canWrite = useCanWrite()

  const approve = useApproveBooking()
  const waitlist = useWaitlistBooking()
  const decline = useDeclineBooking()
  const cancel = useCancelBooking()
  const checkIn = useCheckIn()
  const checkOut = useCheckOut()
  const assignBerth = useAssignBerth()

  const [assignOpened, assignH] = useDisclosure(false)
  const [declineOpened, declineH] = useDisclosure(false)

  if (isLoading) {
    return (
      <Container size="xl" py="md">
        <Stack gap="lg">
          <Skeleton height={28} width={220} radius="sm" />
          <Skeleton height={120} radius="md" />
          <Grid>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Skeleton height={160} radius="md" />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Skeleton height={160} radius="md" />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 4 }}>
              <Skeleton height={160} radius="md" />
            </Grid.Col>
          </Grid>
          <Skeleton height={180} radius="md" />
        </Stack>
      </Container>
    )
  }

  if (!data) {
    return (
      <Container size="xl" py="md">
        <Card padding="lg" radius="md">
          <Stack align="center" gap="sm" py="xl">
            <Text size="lg" fw={600} c="dimmed">
              {t('page.reservations.detail.not_found')}
            </Text>
            <Anchor component={Link} to="/dashboard/reservations/bookings" c="laut" size="sm">
              {t('page.reservations.detail.back_link')}
            </Anchor>
          </Stack>
        </Card>
      </Container>
    )
  }

  const b = data.booking
  const customerName = b.customer ? `${b.customer.firstName} ${b.customer.lastName}`.trim() : '—'

  const durationDays = dayjs(b.checkOutAt).diff(dayjs(b.checkInAt), 'day')
  const durationHours = dayjs(b.checkOutAt).diff(dayjs(b.checkInAt), 'hour') % 24
  const durationStr =
    durationDays > 0
      ? `${durationDays} hari${durationHours > 0 ? ` ${durationHours} jam` : ''}`
      : `${dayjs(b.checkOutAt).diff(dayjs(b.checkInAt), 'hour')} jam`

  const notify = async (p: Promise<unknown>, success: string) => {
    try {
      await p
      notifications.show({ title: 'Berhasil', message: success, color: 'teal' })
    } catch (e: unknown) {
      notifications.show({ title: 'Gagal', message: e instanceof Error ? e.message : 'Coba lagi', color: 'red' })
    }
  }

  const confirmAction = (title: string, msg: string, fn: () => Promise<void>, color: 'teal' | 'red' = 'teal') =>
    modals.openConfirmModal({
      title,
      children: <Text size="sm">{msg}</Text>,
      labels: { confirm: title, cancel: 'Batal' },
      confirmProps: { color },
      onConfirm: fn,
    })

  // Timeline steps
  const isConfirmed = ['CONFIRMED', 'RESERVED', 'CHECKED_IN', 'CHECKED_OUT'].includes(b.status)
  const isBerthAssigned = !!b.berth
  const isCheckedIn = !!b.actualCheckIn || b.status === 'CHECKED_IN' || b.status === 'CHECKED_OUT'
  const isCheckedOut = !!b.actualCheckOut || b.status === 'CHECKED_OUT'

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        {/* Header */}
        <Group gap="sm" align="center">
          <Anchor component={Link} to="/dashboard/reservations/bookings" c="dimmed" style={{ display: 'flex' }}>
            <LuArrowLeft size={18} />
          </Anchor>
          <ThemeIcon variant="light" color="laut" size="lg" radius="md">
            <LuCalendar size={18} />
          </ThemeIcon>
          <div>
            <Title order={2} ff="var(--font-display)" fw={600}>
              {t('page.reservations.detail.title')}
            </Title>
            <Text size="xs" c="dimmed">
              {t('page.reservations.detail.subtitle', { code: b.code })}
            </Text>
          </div>
        </Group>

        {/* Hero card */}
        <Card padding="lg" radius="md">
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Group gap="md" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
              <CustomerAvatar name={customerName} email={b.customer?.email} size={56} />
              <Stack gap={4} style={{ minWidth: 0, flex: 1 }}>
                <Group gap="xs" wrap="wrap">
                  <Text fw={700} size="lg">
                    {customerName}
                  </Text>
                  <StatusBadge status={b.status} kind="booking" upper />
                  {b.customer?.category && <KindBadge category={b.customer.category} />}
                </Group>
                <Group gap="xs" wrap="wrap">
                  {b.boat?.name && (
                    <Text size="sm" c="dimmed">
                      {b.boat.name}
                    </Text>
                  )}
                  {b.berth?.code && (
                    <>
                      <Text size="sm" c="dimmed">
                        ·
                      </Text>
                      <Badge size="xs" variant="light" color="laut" leftSection={<LuAnchor size={9} />}>
                        Berth {b.berth.code}
                      </Badge>
                    </>
                  )}
                </Group>
                <Group gap="xs" wrap="wrap">
                  <LuCalendar size={13} color="var(--mantine-color-dimmed)" />
                  <Text size="sm" c="dimmed">
                    {dayjs(b.checkInAt).format('DD MMM YYYY, HH:mm')} →{' '}
                    {dayjs(b.checkOutAt).format('DD MMM YYYY, HH:mm')}
                  </Text>
                  <Badge size="xs" variant="light" color="gray">
                    {durationStr}
                  </Badge>
                  {b.totalAmountIdr && <CurrencyText idr={b.totalAmountIdr} size="sm" fw={600} c="laut.7" />}
                </Group>
              </Stack>
            </Group>

            {canWrite && (
              <Stack gap="xs" align="flex-end" style={{ flexShrink: 0 }}>
                {(b.status === 'PENDING' || b.status === 'WAITLISTED') && (
                  <Button
                    size="xs"
                    color="laut"
                    onClick={() =>
                      confirmAction(
                        t('page.reservations.detail.action_approve'),
                        t('page.reservations.detail.confirm_approve', { code: b.code }),
                        () => notify(approve.mutateAsync(b.id), t('page.reservations.detail.notify_approved')),
                      )
                    }
                  >
                    {t('page.reservations.detail.action_approve')}
                  </Button>
                )}
                {b.status === 'PENDING' && (
                  <Button
                    size="xs"
                    variant="default"
                    onClick={() =>
                      confirmAction(
                        t('page.reservations.detail.action_waitlist'),
                        t('page.reservations.detail.confirm_waitlist', { code: b.code }),
                        () => notify(waitlist.mutateAsync(b.id), t('page.reservations.detail.notify_waitlisted')),
                      )
                    }
                  >
                    {t('page.reservations.detail.action_waitlist')}
                  </Button>
                )}
                {b.status === 'CONFIRMED' && (
                  <Button size="xs" color="laut" onClick={assignH.open}>
                    {t('page.reservations.detail.action_assign')}
                  </Button>
                )}
                {b.status === 'RESERVED' && (
                  <Button
                    size="xs"
                    color="laut"
                    onClick={() =>
                      confirmAction(
                        t('page.reservations.detail.action_checkin'),
                        t('page.reservations.detail.confirm_checkin', { code: b.code }),
                        () => notify(checkIn.mutateAsync({ id: b.id }), t('page.reservations.detail.notify_checkin')),
                      )
                    }
                  >
                    {t('page.reservations.detail.action_checkin')}
                  </Button>
                )}
                {b.status === 'CHECKED_IN' && (
                  <Button
                    size="xs"
                    color="karang"
                    onClick={() =>
                      confirmAction(
                        t('page.reservations.detail.action_checkout'),
                        t('page.reservations.detail.confirm_checkout', { code: b.code }),
                        () => notify(checkOut.mutateAsync({ id: b.id }), t('page.reservations.detail.notify_checkout')),
                      )
                    }
                  >
                    {t('page.reservations.detail.action_checkout')}
                  </Button>
                )}
                {(b.status === 'PENDING' || b.status === 'WAITLISTED') && (
                  <Button size="xs" variant="light" color="red" onClick={declineH.open}>
                    {t('page.reservations.detail.action_decline')}
                  </Button>
                )}
                {['CONFIRMED', 'RESERVED', 'CHECKED_IN', 'PENDING', 'WAITLISTED'].includes(b.status) && (
                  <Button
                    size="xs"
                    variant="subtle"
                    color="red"
                    onClick={() =>
                      confirmAction(
                        t('page.reservations.detail.action_cancel'),
                        t('page.reservations.detail.confirm_cancel', { code: b.code }),
                        () => notify(cancel.mutateAsync(b.id), t('page.reservations.detail.notify_cancelled')),
                        'red',
                      )
                    }
                  >
                    {t('page.reservations.detail.action_cancel')}
                  </Button>
                )}
              </Stack>
            )}
          </Group>

          {b.notes && (
            <Box
              mt="md"
              p="sm"
              style={{
                borderRadius: 8,
                background: 'light-dark(var(--mantine-color-yellow-0), rgba(250,200,50,0.08))',
                borderLeft: '3px solid var(--mantine-color-yellow-4)',
              }}
            >
              <Text size="xs" c="dimmed" mb={2}>
                {t('page.reservations.detail.notes_label')}
              </Text>
              <Text size="sm">{b.notes}</Text>
            </Box>
          )}
        </Card>

        {/* Info grid */}
        <Grid gutter="md">
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Card padding="lg" radius="md" h="100%">
              <Group gap="xs" mb="sm">
                <ThemeIcon variant="light" color="laut" size="sm" radius="sm">
                  <LuUser size={12} />
                </ThemeIcon>
                <Text fw={600} size="sm">
                  {t('page.reservations.detail.section_customer')}
                </Text>
              </Group>
              <Stack gap={6}>
                <InfoRow
                  label={t('page.reservations.detail.field_name')}
                  value={
                    b.customer?.id ? (
                      <Anchor
                        component={Link}
                        to={`/dashboard/accounts/customers/${b.customer.id}` as any}
                        c="laut"
                        size="sm"
                      >
                        {customerName}
                      </Anchor>
                    ) : (
                      customerName
                    )
                  }
                />
                <InfoRow label={t('page.reservations.detail.field_email')} value={b.customer?.email ?? '—'} />
                <InfoRow label={t('page.reservations.detail.field_phone')} value={(b.customer as any)?.phone ?? '—'} />
                <InfoRow
                  label={t('page.reservations.detail.field_country')}
                  value={(b.customer as any)?.country ?? '—'}
                />
              </Stack>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 4 }}>
            <Card padding="lg" radius="md" h="100%">
              <Group gap="xs" mb="sm">
                <ThemeIcon variant="light" color="laut" size="sm" radius="sm">
                  <LuShip size={12} />
                </ThemeIcon>
                <Text fw={600} size="sm">
                  {t('page.reservations.detail.section_boat')}
                </Text>
              </Group>
              <Stack gap={6}>
                <InfoRow label={t('page.reservations.detail.field_boat_name')} value={b.boat?.name ?? '—'} />
                <InfoRow
                  label={t('page.reservations.detail.field_type')}
                  value={b.boat?.type ? (BOAT_TYPE_ID[b.boat.type] ?? b.boat.type) : '—'}
                />
                <InfoRow
                  label={t('page.reservations.detail.field_loa')}
                  value={b.boat?.loaMeters ? `${(Number(b.boat.loaMeters) * FT_PER_METER).toFixed(1)} ft` : '—'}
                />
                <InfoRow label={t('page.reservations.detail.field_flag')} value={(b.boat as any)?.flagState ?? '—'} />
              </Stack>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 4 }}>
            <Card padding="lg" radius="md" h="100%">
              <Group gap="xs" mb="sm">
                <ThemeIcon variant="light" color="laut" size="sm" radius="sm">
                  <LuMapPin size={12} />
                </ThemeIcon>
                <Text fw={600} size="sm">
                  {t('page.reservations.detail.section_berth')}
                </Text>
              </Group>
              <Stack gap={6}>
                <InfoRow label={t('page.reservations.detail.field_berth')} value={b.berth?.code ?? '—'} />
                <InfoRow label={t('page.reservations.detail.field_pontoon')} value={b.berth?.pontoon?.code ?? '—'} />
                <InfoRow
                  label={t('page.reservations.detail.field_channel')}
                  value={CHANNEL_ID[b.channel] ?? b.channel}
                />
                <InfoRow
                  label={t('page.reservations.detail.field_location')}
                  value={b.channelLocation ?? 'Marina Office'}
                />
              </Stack>
            </Card>
          </Grid.Col>
        </Grid>

        {/* Timeline */}
        <Card padding="lg" radius="md">
          <Group gap="xs" mb="md">
            <ThemeIcon variant="light" color="laut" size="sm" radius="sm">
              <LuClock size={12} />
            </ThemeIcon>
            <Text fw={600} size="sm">
              {t('page.reservations.detail.section_timeline')}
            </Text>
          </Group>
          <Group gap={0} wrap="nowrap" style={{ overflowX: 'auto', paddingBottom: 4 }}>
            {[
              {
                label: t('page.reservations.detail.timeline_created'),
                value: dayjs(b.createdAt).format('DD MMM, HH:mm'),
                done: true,
                active: b.status === 'PENDING' || b.status === 'WAITLISTED',
              },
              {
                label: t('page.reservations.detail.timeline_confirmed'),
                value: isConfirmed ? dayjs(b.updatedAt).format('DD MMM') : '—',
                done: isConfirmed,
                active: b.status === 'CONFIRMED',
              },
              {
                label: t('page.reservations.detail.timeline_berth'),
                value: b.berth?.code ?? '—',
                done: isBerthAssigned,
                active: b.status === 'RESERVED',
              },
              {
                label: t('page.reservations.detail.timeline_checkin'),
                value: b.actualCheckIn
                  ? dayjs(b.actualCheckIn).format('DD MMM, HH:mm')
                  : t('page.reservations.detail.timeline_planned', { date: dayjs(b.checkInAt).format('DD MMM') }),
                done: isCheckedIn,
                active: b.status === 'CHECKED_IN',
              },
              {
                label: t('page.reservations.detail.timeline_checkout'),
                value: b.actualCheckOut
                  ? dayjs(b.actualCheckOut).format('DD MMM, HH:mm')
                  : t('page.reservations.detail.timeline_planned', { date: dayjs(b.checkOutAt).format('DD MMM') }),
                done: isCheckedOut,
                active: false,
              },
            ].map((step, i, arr) => (
              <Group key={step.label} gap={0} wrap="nowrap" align="flex-start" style={{ flex: 1, minWidth: 80 }}>
                <Stack gap={4} align="center" style={{ flex: 1 }}>
                  <ThemeIcon
                    size={28}
                    radius="xl"
                    color={step.done ? 'teal' : step.active ? 'laut' : 'gray'}
                    variant={step.done || step.active ? 'filled' : 'light'}
                  >
                    {step.done ? <LuCheck size={13} /> : <LuClock size={12} />}
                  </ThemeIcon>
                  <Text
                    size="xs"
                    fw={step.active ? 700 : 500}
                    ta="center"
                    c={step.active ? 'laut.7' : step.done ? undefined : 'dimmed'}
                  >
                    {step.label}
                  </Text>
                  <Text size="xs" c="dimmed" ta="center" style={{ wordBreak: 'break-word' }}>
                    {step.value}
                  </Text>
                </Stack>
                {i < arr.length - 1 && (
                  <Box
                    style={{
                      height: 2,
                      width: 32,
                      background: step.done ? 'var(--mantine-color-teal-3)' : 'var(--mantine-color-gray-2)',
                      marginTop: 13,
                      flexShrink: 0,
                    }}
                  />
                )}
              </Group>
            ))}
          </Group>

          {(b.status === 'DECLINED' || b.status === 'CANCELLED' || b.status === 'NO_SHOW') && (
            <Box
              mt="md"
              p="sm"
              style={{ borderRadius: 8, background: 'light-dark(var(--mantine-color-red-0), rgba(220,50,50,0.08))' }}
            >
              <Text size="sm" c="red.7" fw={500}>
                {b.status === 'DECLINED'
                  ? t('page.reservations.detail.terminal_declined')
                  : b.status === 'CANCELLED'
                    ? t('page.reservations.detail.terminal_cancelled')
                    : t('page.reservations.detail.terminal_noshow')}
              </Text>
            </Box>
          )}
        </Card>

        {/* Linked documents */}
        {((b.quotes?.length ?? 0) > 0 || (b.invoices?.length ?? 0) > 0) && (
          <Card padding="lg" radius="md">
            <Group gap="xs" mb="md">
              <ThemeIcon variant="light" color="laut" size="sm" radius="sm">
                <LuFileText size={12} />
              </ThemeIcon>
              <Text fw={600} size="sm">
                {t('page.reservations.detail.section_documents')}
              </Text>
            </Group>
            <Stack gap="xs">
              {b.quotes?.map((q) => (
                <Group key={q.id} justify="space-between">
                  <Group gap="xs">
                    <Badge size="xs" variant="light" color="gray">
                      Penawaran
                    </Badge>
                    <Anchor
                      component={Link}
                      to={`/dashboard/billing/quotes/${q.id}` as any}
                      c="laut"
                      size="sm"
                      fw={500}
                    >
                      {q.code}
                    </Anchor>
                  </Group>
                  <Group gap="xs">
                    <StatusBadge status={q.status} kind="quote" />
                    <CurrencyText idr={q.totalIdr} size="xs" c="dimmed" />
                  </Group>
                </Group>
              ))}
              {b.invoices?.map((inv) => (
                <Group key={inv.id} justify="space-between">
                  <Group gap="xs">
                    <Badge size="xs" variant="light" color="laut">
                      Faktur
                    </Badge>
                    <Anchor
                      component={Link}
                      to={`/dashboard/billing/invoices/${inv.id}` as any}
                      c="laut"
                      size="sm"
                      fw={500}
                    >
                      {inv.code}
                    </Anchor>
                  </Group>
                  <Group gap="xs">
                    <StatusBadge status={inv.status} kind="invoice" />
                    <CurrencyText idr={inv.totalIdr} size="xs" c="dimmed" />
                  </Group>
                </Group>
              ))}
            </Stack>
          </Card>
        )}
      </Stack>

      <AssignBerthModal
        opened={assignOpened}
        onClose={assignH.close}
        booking={b}
        onSubmit={async (berthId) => {
          await notify(assignBerth.mutateAsync({ id: b.id, berthId }), t('page.reservations.detail.modal_assign_btn'))
          assignH.close()
        }}
      />

      <DeclineModal
        opened={declineOpened}
        onClose={declineH.close}
        onSubmit={async (reason) => {
          await notify(decline.mutateAsync({ id: b.id, reason }), t('page.reservations.detail.modal_decline_btn'))
          declineH.close()
        }}
      />
    </Container>
  )
}

function AssignBerthModal({
  opened,
  onClose,
  booking,
  onSubmit,
}: {
  opened: boolean
  onClose: () => void
  booking: Booking
  onSubmit: (berthId: string) => void
}) {
  const { t } = useTranslation()
  const { data } = useBerths({ status: 'AVAILABLE' })
  const [berthId, setBerthId] = useState<string | null>(null)
  const berths = data?.items ?? []
  const selected = berths.find((b) => b.id === berthId)

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={t('page.reservations.detail.modal_assign_title', { code: booking.code })}
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {t('page.reservations.detail.modal_assign_desc')}
        </Text>
        <Select
          label={t('page.reservations.detail.modal_assign_title', { code: booking.code })}
          placeholder={t('page.reservations.detail.modal_assign_placeholder')}
          searchable
          data={berths.map((br) => ({
            value: br.id,
            label: `${br.code}${br.pontoon?.name ? ` — ${br.pontoon.name}` : ''}`,
          }))}
          value={berthId}
          onChange={setBerthId}
        />
        {selected && (
          <Box
            p="sm"
            style={{ borderRadius: 8, background: 'light-dark(var(--mantine-color-laut-0), rgba(14,124,123,0.12))' }}
          >
            <Text size="sm" fw={600}>
              {selected.code}
            </Text>
            <Text size="xs" c="dimmed">
              Maks LOA {Number(selected.maxLoaMeters).toFixed(1)} m · Beam {Number(selected.maxBeamMeters).toFixed(1)} m
              · Dalam {Number(selected.depthMeters).toFixed(1)} m
            </Text>
          </Box>
        )}
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button color="laut" disabled={!berthId} onClick={() => berthId && onSubmit(berthId)}>
            {t('page.reservations.detail.modal_assign_btn')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

function DeclineModal({
  opened,
  onClose,
  onSubmit,
}: {
  opened: boolean
  onClose: () => void
  onSubmit: (reason: string) => void
}) {
  const { t } = useTranslation()
  const [reason, setReason] = useState('')
  return (
    <Modal opened={opened} onClose={onClose} title={t('page.reservations.detail.modal_decline_title')}>
      <Stack gap="md">
        <Textarea
          label={t('page.reservations.detail.modal_decline_label')}
          required
          rows={3}
          placeholder={t('page.reservations.detail.modal_decline_placeholder')}
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button color="red" disabled={!reason.trim()} onClick={() => onSubmit(reason.trim())}>
            {t('page.reservations.detail.modal_decline_btn')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Group justify="space-between" gap="xs">
      <Text size="sm" c="dimmed" style={{ flexShrink: 0 }}>
        {label}
      </Text>
      <Text size="sm" ta="right" style={{ maxWidth: '60%' }}>
        {value || '—'}
      </Text>
    </Group>
  )
}
